import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
} from "@mui/material";

export interface MissingDocumentItem {
  name: string;
  key?: string;
  id?: number;
  maxDayToUpload?: number | null;
  deadline?: string;
  isOverdue?: boolean;
}

export interface MissingDocumentsModalProps {
  open: boolean;
  list: MissingDocumentItem[];
  onClose?: () => void;
  onConfirm: () => void;
}

const formatDeadline = (iso?: string) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const MissingDocumentsModal: React.FC<MissingDocumentsModalProps> = ({
  open,
  list,
  onClose,
  onConfirm,
}) => {
  const overdueDocs = list.filter((d) => d.isOverdue === true);
  const otherMissing = list.filter((d) => !d.isOverdue);
  const reminderText =
    overdueDocs.length > 0
      ? `Debes subir ${overdueDocs.map((d) => d.name).join(", ")} para desbloquear tu asistencia.`
      : "Completa tu expediente en la app o con Talento Humano para poder checar.";

  const handleClose = () => {
    onConfirm();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth={false}
      aria-labelledby="missing-docs-dialog-title"
      PaperProps={{
        sx: {
          width: 600,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: 4,
          boxShadow: "0 20px 45px rgba(15,23,42,0.25)",
          overflow: "hidden",
          position: "relative",
          animation: `${open ? "bounceFadeIn" : "bounceFadeOut"} 380ms ease-out`,
          "@keyframes bounceFadeIn": {
            "0%": { opacity: 0, transform: "translateY(-12px) scale(0.9)" },
            "55%": { opacity: 1, transform: "translateY(4px) scale(1.03)" },
            "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
          },
          "@keyframes bounceFadeOut": {
            "0%": { opacity: 1, transform: "translateY(0) scale(1)" },
            "40%": { opacity: 1, transform: "translateY(-4px) scale(1.02)" },
            "100%": { opacity: 0, transform: "translateY(10px) scale(0.9)" },
          },
        },
      }}
    >
      <DialogTitle
        id="missing-docs-dialog-title"
        sx={{ position: "absolute", visibility: "hidden", height: 0, overflow: "hidden" }}
      >
        No puedes checar asistencia – Documentos faltantes
      </DialogTitle>

      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: "flex",
          alignItems: "center",
          gap: 2,
          bgcolor: "#FFB74D",
          color: "#311B0B",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.95)",
            border: "3px solid rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2rem",
            color: "#FF9800",
            flexShrink: 0,
          }}
          aria-hidden
        >
          !
        </Box>

        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#2D1603",
              letterSpacing: 0.1,
            }}
          >
            No puedes checar asistencia
          </Typography>
          <Typography variant="body2" sx={{ color: "#4E342E", mt: 0.25 }}>
            Tienes documentación inmediata pendiente en tu expediente.
          </Typography>
        </Box>
      </Box>

      <DialogContent
        sx={{
          pt: 2.75,
          pb: 3,
          px: 3.25,
          textAlign: "center",
          bgcolor: "#F5F7FB",
        }}
      >

        {list.length > 0 && (
          <>
            {overdueDocs.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 600, textAlign: "left" }}
                >
                  Vencieron el plazo (debes subirlos para desbloquear):
                </Typography>
                <List
                  dense
                  disablePadding
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    textAlign: "left",
                  }}
                >
                  {overdueDocs.map((doc, idx) => (
                    <ListItem
                      key={doc.id ?? doc.key ?? `overdue-${idx}`}
                      disablePadding
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        borderRadius: 1.75,
                        bgcolor: "#FFEBEE",
                        border: "1px solid",
                        borderColor: "#E57373",
                      }}
                    >
                      <ListItemText
                        primary={doc.name}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          variant: "body2",
                          color: "#C62828",
                        }}
                        secondary={
                          doc.deadline
                            ? `Vencía: ${formatDeadline(doc.deadline)}`
                            : null
                        }
                        secondaryTypographyProps={{
                          variant: "caption",
                          sx: { color: "#B71C1C", fontWeight: 500 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {otherMissing.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1.25, fontWeight: 600, textAlign: "left" }}
                >
                  {overdueDocs.length > 0 ? "También te faltan:" : "Te faltan los siguientes documentos:"}
                </Typography>
                <List
                  dense
                  disablePadding
                  sx={{
                    mb: 2.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    textAlign: "left",
                    maxHeight: 220,
                    overflowY: list.length > 4 ? "auto" : "visible",
                  }}
                >
                  {otherMissing.map((doc, idx) => (
                    <ListItem
                      key={doc.id ?? doc.key ?? idx}
                      disablePadding
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        borderRadius: 1.75,
                        bgcolor: "#FFFFFF",
                        border: "1px solid",
                        borderColor: "#E1E5EE",
                      }}
                    >
                      <ListItemText
                        primary={doc.name}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          variant: "body2",
                        }}
                        secondary={
                          doc.maxDayToUpload != null && doc.maxDayToUpload > 0
                            ? `${doc.maxDayToUpload} ${
                                doc.maxDayToUpload === 1 ? "día" : "días"
                              } máx.`
                            : "Sin límite"
                        }
                        secondaryTypographyProps={{
                          variant: "caption",
                          sx: { color: "#F57C00", fontWeight: 500 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}

        <Box
          sx={{
            mt: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.75,
            bgcolor: "#E3F2FD",
            color: "#1565C0",
            border: "1px solid",
            borderColor: "#90CAF9",
            textAlign: "left",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {reminderText}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.75,
          pb: 3,
          pt: 1,
          justifyContent: "center",
          bgcolor: "#F5F7FB",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          sx={{
            minWidth: 160,
            py: 1.15,
            borderRadius: 999,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 5px 14px rgba(25, 118, 210, 0.35)",
          }}
        >
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingDocumentsModal;

